import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowUpRight, Check } from 'react-feather';
import { TIERS, TIER_META, readPlanTier, nextPlanTier } from '../../../util/coachTier';
import css from './UpgradeGate.module.css';

/**
 * UpgradeGate — shown in place of a gated feature when the current
 * coach is below the required plan tier.
 *
 * Used by Coach Branding, Verification, and Reports pages. Pattern:
 *
 *   if (!meetsTierGate(currentUser, TIERS.VERIFIED)) {
 *     return (
 *       <UpgradeGate
 *         featureName="Coach Branding"
 *         requiredTier={TIERS.VERIFIED}
 *         currentUser={currentUser}
 *         benefits={[
 *           'Customise your client-facing colors and logo',
 *           'Brand your emails and session pages',
 *           'Build a recognisable coaching practice',
 *         ]}
 *         preview="/screenshots/branding-preview.png"
 *       />
 *     );
 *   }
 *
 * Props:
 *   featureName  — string shown in the headline ("Coach Branding is a
 *                  Verified feature")
 *   requiredTier — one of TIERS.* — the minimum tier needed
 *   currentUser  — the Sharetribe user object (for tier read)
 *   benefits     — array of strings, shown as a checklist
 *   preview      — optional image URL for a screenshot preview
 *   ctaUrl       — defaults to /account/payments/pricing
 */
function UpgradeGate({
  featureName,
  requiredTier,
  currentUser,
  benefits = [],
  preview,
  ctaUrl = '/account/payments/pricing',
}) {
  const currentTier = readPlanTier(currentUser);
  const requiredMeta = TIER_META[requiredTier] || TIER_META[TIERS.VERIFIED];
  const nextTier = nextPlanTier(currentUser) || requiredTier;
  const nextMeta = TIER_META[nextTier] || requiredMeta;

  return (
    <div className={css.base}>
      <div className={css.heroCard}>
        <div className={css.lockBadge}>
          <Lock size={20} />
        </div>
        <div className={css.eyebrow}>
          {requiredMeta.label} feature
        </div>
        <h1 className={css.headline}>
          {featureName} is unlocked at <span className={css.tierName}>{requiredMeta.label}</span>
        </h1>
        <p className={css.subheadline}>
          You're currently on the <strong>{TIER_META[currentTier].label}</strong> plan.
          Upgrade to {nextMeta.label} to unlock {featureName.toLowerCase()} and the rest of the
          tier's benefits.
        </p>

        {benefits.length > 0 && (
          <ul className={css.benefitsList}>
            {benefits.map((b, i) => (
              <li key={i} className={css.benefitRow}>
                <span className={css.benefitCheck}>
                  <Check size={14} />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        <Link to={ctaUrl} className={css.ctaButton}>
          Upgrade to {nextMeta.label}
          <ArrowUpRight size={18} />
        </Link>

        <div className={css.footnote}>
          Already paid? Your tier may take a few minutes to update.
          {' '}
          <a href="mailto:support@viveka.world" className={css.footnoteLink}>Contact support</a>
          {' '}if it doesn't.
        </div>
      </div>

      {preview && (
        <div className={css.previewWrap}>
          <div className={css.previewLabel}>Preview</div>
          <img src={preview} alt={`${featureName} preview`} className={css.previewImage} />
          <div className={css.previewOverlay}>
            <Lock size={28} />
            <span>Unlock with {nextMeta.label}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpgradeGate;
